'use client';

import Link from 'next/link';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { cmsColorClass } from '@/utils/cmsColorClass';
import { jsonToHtml } from '@contentstack/json-rte-serializer';
import Container from '@/components/elements/Container';
import { openZaiusCampaign } from '@/components/integrations/Zaius/Modal';

// Skips customHTMLSerializer's `a` handler, which hardcodes a link color that fails
// contrast on this bg-primary background and doesn't add underline. Also strips the
// stray `url` attr the RTE serializer leaves behind once a custom `a` handler is registered.
const outboundLinkSerializer = {
  customElementTypes: {
    a: (attrs, child) => {
      const cleanAttrs = attrs.replace(/\s*url="[^"]*"/, '');
      return attrs.includes('target="_blank"')
        ? `<a${cleanAttrs} class="underline">${child}<span class="sr-only"> (opens in a new tab)</span></a>`
        : `<a${cleanAttrs} class="underline">${child}</a>`;
    },
  },
};

const UtilityBar = () => {
  const { settings, campaign } = useSiteSettings();

  if (!settings?.utilityNav) return null;

  const { button_label, message, mobile_message, navigation } = settings.utilityNav;

  // BL-743: the bar follows the campaign's colours so the two read as one
  // scheme. Both fall back to what was hardcoded here before, which is what a
  // site with no campaign running still gets.
  const campaignSettings = campaign?.campaign_banner?.settings;
  const barBackground = cmsColorClass(
    campaignSettings?.background_color?.color_selector,
    'bg-primary',
  );
  const barText = cmsColorClass(campaignSettings?.text_color?.color_selector, 'text-white');

  // The message and the nav links carry no colour of their own, so they inherit
  // `barText` from the bar. The CTA has to be spelled out: its border stays on
  // the text colour in both states, and the hover swaps the other two — fill in
  // the text colour, label in the bar's background.
  //
  // All three name their token rather than leaning on `current`. `border-current`
  // and `hover:bg-current` both read as the obvious way to write this and both
  // are wrong: `currentColor` resolves after the hover has already changed
  // `color`, so on hover the fill and the border land on the hover colour, the
  // button turns the same shade as the bar and disappears.
  const textToken = barText.replace(/^text-/, '');
  const borderColor = `border-${textToken}`;
  const hoverFill = `hover:bg-${textToken}`;
  const hoverLabel = `hover:text-${barBackground.replace(/^bg-/, '')}`;
  const contentHtml = jsonToHtml(message, outboundLinkSerializer);
  const mobileContentHtml = jsonToHtml(mobile_message, outboundLinkSerializer);

  // BL-744: production runs this bar at 37px on desktop and ~65px on mobile;
  // this was 48px and 86px. Desktop is a fixed height, so h-[37px] rather than
  // a scale step — h-9 is 36 and h-10 is 40, neither lands on it, and the file
  // already uses arbitrary heights elsewhere. Mobile has no fixed height: it is
  // padding plus the message, the mt-2 gap and the button, so the padding is
  // what gets trimmed.
  return (
    <div className={`${barBackground} ${barText} text-center md:text-left py-1.5 md:py-0 md:h-[37px] flex items-center`}>
      <Container className="md:flex md:items-center md:justify-center xl:justify-between w-full">
        {/* Promo message + CTA */}
        <div className="md:flex md:items-center md:gap-4">
          <div
            className="block md:hidden [&_strong]:font-semibold [&_em]:italic text-sm"
            dangerouslySetInnerHTML={{ __html: mobileContentHtml }}
          />
          <div
            className="hidden md:inline-block [&_strong]:font-semibold [&_em]:italic text-sm"
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />
          {button_label && (
            <button
              onClick={openZaiusCampaign}
              className={`border ${borderColor} rounded-full px-4 py-0.5 whitespace-nowrap cursor-pointer text-sm ${hoverFill} ${hoverLabel} transition-colors mt-2 md:mt-0`}
            >
              {button_label}
            </button>
          )}
        </div>

        {/* Nav links — desktop only */}
        {navigation?.length > 0 && (
          // These follow the header's breakpoint, not their own: the main nav is
          // `hidden xl:flex` and the hamburger `xl:hidden`, so between lg and xl
          // the links were still on screen while the nav had already collapsed,
          // squeezing the campaign message onto a second line. `justify-between`
          // moves with them — it exists to push this nav right, so with the nav
          // gone the message goes back to centred rather than flush left.
          <nav className="hidden xl:flex items-center gap-6 text-sm flex-shrink-0" aria-label="Utility navigation">
            {navigation.map((item) => {
              const external = /^https?:\/\//.test(item.link.href);
              return (
                <Link
                  key={item._metadata?.uid}
                  href={item.link.href}
                  // BL-467: the text is 20px tall, under the 24px minimum. The
                  // bar is a fixed h-12 with items-center, so padding grows the
                  // hit area without moving anything on screen.
                  className="whitespace-nowrap hover:underline inline-flex items-center py-1"
                  {...(external && { target: '_blank', rel: 'noopener noreferrer' })}
                >
                  {item.navigation_item_label}
                  {external && <span className="sr-only"> (opens in a new tab)</span>}
                </Link>
              );
            })}
          </nav>
        )}
      </Container>
    </div>
  );
};

export default UtilityBar;
